import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  output,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatListModule, MatSelectionListChange } from '@angular/material/list';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import {
  AuthService,
  createSearchResultGeometry,
  QUICK_SEARCH_MIN_CHAR_LENGTH,
  QUICK_SEARCH_SCROLL_THRESHOLD,
  QuickSearchResult,
  QuickSearchService,
  QuickSearchStore,
  SearchResultService,
} from '@lassi/webapp-shared';
import { QuickSearchResultItemComponent } from './quick-search-result-item.component';

@Component({
  selector: 'lassi-quick-search-results',
  imports: [
    MatFormFieldModule,
    FormsModule,
    MatInputModule,
    MatChipsModule,
    MatMenuModule,
    MatButtonModule,
    MatListModule,
    QuickSearchResultItemComponent,
  ],
  template: `
    <div
      [style.width.px]="width()"
      class="results-container menu-container quick-search-results"
      (scroll)="handleResultsScroll($event)"
    >
      @if (
        !store.query() || store.query()!.length < QUICK_SEARCH_MIN_CHAR_LENGTH
      ) {
        <p class="hint">Please keep typing to search</p>
      } @else {
        @if (isLoggedIn()) {
          <mat-selection-list
            (click)="$event.stopPropagation()"
            (selectionChange)="handleResultSelectionChange($event)"
          >
            @for (result of store.items(); track $index) {
              <mat-list-option
                class="list-option"
                [value]="result"
                [selected]="
                  !!searchResultService.searchResults()[result.doc_id!]
                "
              >
                <lassi-quick-search-result-item
                  [result]="result"
                  [query]="store.query()"
                />
              </mat-list-option>
            } @empty {
              @if (!store.isLoading()) {
                <p class="hint">Could not find any results</p>
              }
            }
            @if (store.isLoading()) {
              <p class="hint">Loading...</p>
            }
          </mat-selection-list>
        } @else {
          <mat-action-list>
            @for (result of store.items(); track $index) {
              <button
                class="list-option"
                mat-list-item
                (click)="handleClickedResult(result, item.resultName())"
              >
                <lassi-quick-search-result-item
                  #item
                  [result]="result"
                  [query]="store.query()"
                />
              </button>
            } @empty {
              @if (!store.isLoading()) {
                <p class="hint">Could not find any results</p>
              }
            }
            @if (store.isLoading()) {
              <p class="hint">Loading...</p>
            }
          </mat-action-list>
        }
      }
      @if (store.items().length || !store.isLoading()) {
        <div class="total-container">
          <p class="mat-label-medium">
            <b>{{ store.total() }}</b> found
          </p>
          @if (landing() && isLoggedIn()) {
            <button class="goToMap" mat-flat-button (click)="goToMap.emit()">
              Go to map
            </button>
          }
        </div>
      }
    </div>
  `,
  styles: `
    @use 'quick-search' as qs;

    @include qs.quickSearch();
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QuickSearchResultsComponent {
  readonly store = inject(QuickSearchStore);
  readonly isLoggedIn = inject(AuthService).isLoggedIn;
  private readonly quickSearchService = inject(QuickSearchService);
  readonly searchResultService = inject(SearchResultService);

  width = input.required<number>();
  readonly landing = input.required<boolean>();
  readonly resultsTrigger = input.required<MatMenuTrigger>();

  readonly goToMap = output<void>();

  readonly QUICK_SEARCH_MIN_CHAR_LENGTH = QUICK_SEARCH_MIN_CHAR_LENGTH;

  handleClickedResult(result: QuickSearchResult, resultName: string | null) {
    this.store.setQuery(resultName ?? '');
    this.resultsTrigger().closeMenu();
    this.goToMap.emit();

    this.quickSearchService.getQuickSearchGeometry(result).subscribe({
      next: (geoJson) => {
        this.searchResultService.replaceSearchResult(
          result.doc_id!,
          createSearchResultGeometry(geoJson),
        );
      },
    });
  }

  handleResultSelectionChange(event: MatSelectionListChange) {
    const option = event.options[0];
    const result = option.value as QuickSearchResult;

    if (option.selected) {
      this.quickSearchService.getQuickSearchGeometry(result).subscribe({
        next: (geoJson) => {
          if (event.source._value && event.source._value.length > 1) {
            this.searchResultService.addSearchResult(
              result.doc_id!,
              createSearchResultGeometry(geoJson),
            );
          } else {
            this.searchResultService.replaceSearchResult(
              result.doc_id!,
              createSearchResultGeometry(geoJson),
            );
          }
        },
      });
    } else {
      this.searchResultService.removeSearchResult(result.doc_id!);
    }
  }

  handleResultsScroll(event: Event) {
    const { target } = event;
    if (!(target instanceof HTMLElement)) return;

    const diff = target.scrollHeight - (target.scrollTop + target.clientHeight);
    if (diff < QUICK_SEARCH_SCROLL_THRESHOLD) {
      this.store.loadMore();
    }
  }
}
